import type { CSSProperties } from 'react';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type AvatarStatus = 'online' | 'busy' | 'away';

const sizes: Record<AvatarSize, number> = { xs: 22, sm: 28, md: 36, lg: 48, xl: 72 };

const statusColors: Record<AvatarStatus, string> = {
    online: 'var(--success)',
    busy: 'var(--danger)',
    away: 'var(--warning)',
};

/** Duas primeiras iniciais do nome. Cai em string vazia sem nome, e o avatar
 *  fica só com o fundo — melhor que exibir um placeholder genérico. */
function initials(name = ''): string {
    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0] ?? '')
        .join('')
        .toUpperCase();
}

export interface AvatarProps {
    name?: string;
    src?: string;
    size?: AvatarSize;
    status?: AvatarStatus;
    style?: CSSProperties;
}

export function Avatar({ name = '', src, size = 'md', status, style }: AvatarProps) {
    const d = sizes[size] ?? sizes.md;
    const statusColor = status ? statusColors[status] : undefined;
    return (
        <span style={{ position: 'relative', display: 'inline-flex', flex: '0 0 auto', ...style }}>
            <span
                style={{
                    width: d, height: d, borderRadius: '50%', overflow: 'hidden', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', background: 'var(--surface-brand-soft)',
                    color: 'var(--text-brand)', fontFamily: 'var(--font-ui)', fontWeight: 600,
                    fontSize: Math.max(10, Math.round(d * 0.36)), letterSpacing: 'var(--tracking-snug)',
                    border: '1px solid var(--burgundy-100)',
                }}
            >
                {src ? <img src={src} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials(name)}
            </span>
            {statusColor && (
                <span
                    style={{
                        position: 'absolute', right: -1, bottom: -1,
                        width: Math.max(7, d * 0.26), height: Math.max(7, d * 0.26),
                        borderRadius: '50%', background: statusColor, border: '2px solid var(--surface)',
                    }}
                />
            )}
        </span>
    );
}
